import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { reportsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users,
  Zap,
  DollarSign,
  Heart,
  TrendingUp,
  ArrowRight,
  Loader2,
  AlertCircle,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user, canManageUsers, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    try {
      const [statsRes, pendingRes] = await Promise.all([
        reportsAPI.getDashboardStats(),
        canManageUsers() ? usersAPI.getPending() : Promise.resolve({ data: [] }),
      ]);
      setStats(statsRes.data);
      setPendingUsers(pendingRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [canManageUsers]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleApproveUser = async (userId) => {
    try {
      await usersAPI.approve(userId);
      toast.success('User approved successfully');
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await usersAPI.reject(userId);
      toast.success('User rejected');
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const getMilestoneColor = (milestone) => {
    const colors = {
      'Lead Captured': 'bg-slate-500',
      'Qualified': 'bg-blue-500',
      'Proposal Sent': 'bg-purple-500',
      'Contract Signed': 'bg-amber-500',
      'Site Survey Completed': 'bg-cyan-500',
      'Installation Scheduled': 'bg-orange-500',
      'Installation Completed': 'bg-emerald-500',
      'PTO / Project Complete': 'bg-green-500',
      'Sale Completed': 'bg-emerald-600',
    };
    return colors[milestone] || 'bg-slate-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Here's what's happening with your solar deals today.
          </p>
        </div>
        <Link to="/leads/new">
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-testid="dashboard-new-lead-btn">
            <Zap className="w-4 h-4 mr-2" />
            Capture New Lead
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 stagger-children">
        <Card className="stats-card" data-testid="stats-total-leads">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total Leads
                </p>
                <p className="text-3xl font-bold font-heading text-slate-900 dark:text-white mt-2">
                  {stats?.total_leads || 0}
                </p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                <Users className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stats-total-kw">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Total kW Sold
                </p>
                <p className="text-3xl font-bold font-heading text-slate-900 dark:text-white mt-2 data-text">
                  {(stats?.total_kw_sold || 0).toFixed(1)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Zap className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stats-card" data-testid="stats-commission">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                  Commission {user?.role === 'SalesRep' ? 'Earned' : 'Owed'}
                </p>
                <p className="text-3xl font-bold font-heading text-slate-900 dark:text-white mt-2 data-text">
                  ${(stats?.total_commission_owed || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        {(isAdmin() || user?.role === 'BackOffice') && (
          <Card className="stats-card" data-testid="stats-nonprofit">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    SUNRAISING Donations
                  </p>
                  <p className="text-3xl font-bold font-heading text-slate-900 dark:text-white mt-2 data-text">
                    ${(stats?.total_nonprofit_donations || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-xl">
                  <Heart className="w-6 h-6 text-rose-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pending Approvals for Admin */}
      {canManageUsers() && pendingUsers.length > 0 && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20" data-testid="pending-approvals-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertCircle className="w-5 h-5" />
              Pending User Approvals ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingUsers.map((pendingUser) => (
                <div
                  key={pendingUser.id}
                  className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border border-amber-200 dark:border-amber-800"
                >
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {pendingUser.name}
                    </p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {pendingUser.email} • {pendingUser.role}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRejectUser(pendingUser.id)}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      data-testid={`reject-user-${pendingUser.id}`}
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleApproveUser(pendingUser.id)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`approve-user-${pendingUser.id}`}
                    >
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pipeline Overview */}
      <Card data-testid="pipeline-overview-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Pipeline Overview</CardTitle>
          <Link to="/pipeline">
            <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
              View Pipeline
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              'Lead Captured',
              'Qualified',
              'Proposal Sent',
              'Contract Signed',
              'Installation Completed',
            ].map((milestone) => (
              <div
                key={milestone}
                className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg text-center"
              >
                <div className={`w-3 h-3 ${getMilestoneColor(milestone)} rounded-full mx-auto mb-2`} />
                <p className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                  {stats?.leads_by_milestone?.[milestone] || 0}
                </p>
                <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                  {milestone}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Leads */}
      <Card data-testid="recent-leads-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Recent Leads</CardTitle>
          <Link to="/leads">
            <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {stats?.recent_leads?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_leads.map((lead) => (
                <Link
                  key={lead.id}
                  to={`/leads/${lead.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  data-testid={`recent-lead-${lead.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">
                        {lead.customer_name}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {lead.city}, {lead.state} • {lead.lead_type}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-medium text-slate-900 dark:text-white">
                      {lead.system_size_kw} kW
                    </p>
                    <Badge variant="secondary" className="mt-1">
                      {lead.status}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No leads yet. Start by capturing your first lead!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
